import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixRestaurantsMenusItemVariantV1VariantsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/restaurants-menus-item-variant',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/restaurants-menus-item-variant',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/restaurants-menus-item-variant',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/restaurants-menus-item-variant',
        destPath: '',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/restaurants-menus-item-variant',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/restaurants-menus-item-variant',
        destPath: '',
      },
      {
        srcPath: '/restaurants/menus/v1/variants',
        destPath: '/v1/variants',
      },
      {
        srcPath: '/restaurants/menus/v1/bulk/variants/create',
        destPath: '/v1/bulk/variants/create',
      },
      {
        srcPath: '/restaurants/menus/v1/bulk/variants/update',
        destPath: '/v1/bulk/variants/update',
      },
      {
        srcPath: '/restaurants/menus/v1/bulk/variants/delete',
        destPath: '/v1/bulk/variants/delete',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/restaurants-menus-item-variant',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/restaurants-menus-item-variant',
        destPath: '',
      },
      {
        srcPath: '/restaurants/menus/v1/variants',
        destPath: '/v1/variants',
      },
      {
        srcPath: '/restaurants/menus/v1/bulk/variants/create',
        destPath: '/v1/bulk/variants/create',
      },
      {
        srcPath: '/restaurants/menus/v1/bulk/variants/update',
        destPath: '/v1/bulk/variants/update',
      },
      {
        srcPath: '/restaurants/menus/v1/bulk/variants/delete',
        destPath: '/v1/bulk/variants/delete',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/restaurants/item-variants',
        destPath: '',
      },
      {
        srcPath: '/restaurants/menus/v1/bulk/variants/create',
        destPath: '/v1/bulk/variants/create',
      },
      {
        srcPath: '/restaurants/menus/v1/bulk/variants/update',
        destPath: '/v1/bulk/variants/update',
      },
      {
        srcPath: '/restaurants/menus/v1/variants',
        destPath: '/v1/variants',
      },
      {
        srcPath: '/restaurants/menus/v1/bulk/variants/delete',
        destPath: '/v1/bulk/variants/delete',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/restaurants-menus-item-variant',
        destPath: '',
      },
    ],
    'apps._base_domain_': [
      {
        srcPath: '/_api/restaurants-menus-item-variant',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_restaurants_item-variants';

/**
 * > **Note:** The Item Variants API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Creates an item variant.
 *
 * To create multiple item variants at once, use [Bulk Create Variants](https://dev.wix.com/docs/rest/business-solutions/restaurants/menus/item-variant/bulk-create-variants).
 */
export function createVariant(payload: object): RequestOptionsFactory<any> {
  function __createVariant({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'variant.createdDate' },
          { path: 'variant.updatedDate' },
          { path: 'variant.externalReferenceInfo.lastSyncDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.menus.v1.item_variant',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.menus_item_variant.v1.VariantsService.CreateVariant',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsMenusItemVariantV1VariantsServiceUrl({
        protoPath: '/v1/variants',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'variant.createdDate' },
              { path: 'variant.updatedDate' },
              { path: 'variant.externalReferenceInfo.lastSyncDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createVariant;
}

/**
 * > **Note:** The Item Variants API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Retrieves an item variant by ID.
 */
export function getVariant(payload: object): RequestOptionsFactory<any> {
  function __getVariant({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.menus.v1.item_variant',
      method: 'GET' as any,
      methodFqn:
        'wix.restaurants.menus_item_variant.v1.VariantsService.GetVariant',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsMenusItemVariantV1VariantsServiceUrl({
        protoPath: '/v1/variants/{variantId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'variant.createdDate' },
              { path: 'variant.updatedDate' },
              { path: 'variant.externalReferenceInfo.lastSyncDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getVariant;
}

/**
 * > **Note:** The Item Variants API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Retrieves a list of up to 500 item variants.
 */
export function listVariants(payload: object): RequestOptionsFactory<any> {
  function __listVariants({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.menus.v1.item_variant',
      method: 'GET' as any,
      methodFqn:
        'wix.restaurants.menus_item_variant.v1.VariantsService.ListVariants',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsMenusItemVariantV1VariantsServiceUrl({
        protoPath: '/v1/variants',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'variants.createdDate' },
              { path: 'variants.updatedDate' },
              { path: 'variants.externalReferenceInfo.lastSyncDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listVariants;
}

/**
 * > **Note:** The Item Variants API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Updates an item variant.
 *
 * To update multiple item variants at once, use [Bulk Update Variants](https://dev.wix.com/docs/rest/business-solutions/restaurants/menus/item-variant/bulk-update-variants).
 *
 * Each time an item variant is updated, its revision increments by 1. The existing revision must be included when updating the variant. This ensures you're working with the latest variant information, and it prevents unintended overwrites.
 */
export function updateVariant(payload: object): RequestOptionsFactory<any> {
  function __updateVariant({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'variant.createdDate' },
          { path: 'variant.updatedDate' },
          { path: 'variant.externalReferenceInfo.lastSyncDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.menus.v1.item_variant',
      method: 'PATCH' as any,
      methodFqn:
        'wix.restaurants.menus_item_variant.v1.VariantsService.UpdateVariant',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsMenusItemVariantV1VariantsServiceUrl({
        protoPath: '/v1/variants/{variant.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'variant.createdDate' },
              { path: 'variant.updatedDate' },
              { path: 'variant.externalReferenceInfo.lastSyncDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateVariant;
}

/**
 * > **Note:** The Item Variants API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Creates multiple item variants.
 */
export function bulkCreateVariants(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkCreateVariants({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'variants.createdDate' },
          { path: 'variants.updatedDate' },
          { path: 'variants.externalReferenceInfo.lastSyncDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.menus.v1.item_variant',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.menus_item_variant.v1.VariantsService.BulkCreateVariants',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsMenusItemVariantV1VariantsServiceUrl({
        protoPath: '/v1/bulk/variants/create',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.variant.createdDate' },
              { path: 'results.variant.updatedDate' },
              { path: 'results.variant.externalReferenceInfo.lastSyncDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkCreateVariants;
}

/**
 * > **Note:** The Item Variants API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Updates multiple item variants.
 *
 * Each time an item variant is updated, its revision increments by 1. The existing revision must be included when updating variant. This ensures you're working with the latest item information, and it prevents unintended overwrites.
 *
 * Up to 100 item variants can be returned per request.
 */
export function bulkUpdateVariants(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateVariants({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'variants.fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'variants.variant.createdDate' },
          { path: 'variants.variant.updatedDate' },
          { path: 'variants.variant.externalReferenceInfo.lastSyncDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.menus.v1.item_variant',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.menus_item_variant.v1.VariantsService.BulkUpdateVariants',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsMenusItemVariantV1VariantsServiceUrl({
        protoPath: '/v1/bulk/variants/update',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.variant.createdDate' },
              { path: 'results.variant.updatedDate' },
              { path: 'results.variant.externalReferenceInfo.lastSyncDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkUpdateVariants;
}

/**
 * > **Note:** The Item Variants API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Permanently deletes an item variant.
 */
export function deleteVariant(payload: object): RequestOptionsFactory<any> {
  function __deleteVariant({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.menus.v1.item_variant',
      method: 'DELETE' as any,
      methodFqn:
        'wix.restaurants.menus_item_variant.v1.VariantsService.DeleteVariant',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsMenusItemVariantV1VariantsServiceUrl({
        protoPath: '/v1/variants/{variantId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteVariant;
}

/**
 * Creates a query to retrieve a list of item variants.
 *
 * The `queryVariants()` function builds a query to retrieve a list of item variants and returns a `VariantsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is used to run the query using the [`find()`](/item-variants/variants-query-builder/find) function.
 *
 * You can refine the query by chaining `VariantsQueryBuilder` functions onto the query. `VariantsQueryBuilder` functions enable you to filter, sort, and control the results that `queryVariants()` returns.
 *
 * `queryVariants()` runs with the following `VariantsQueryBuilder` defaults, which you can override:
 *
 * * [`limit(500)`](/item-variants/variants-query-builder/limit)
 *
 * The following `VariantsQueryBuilder` functions are supported for `queryVariants()`. For a full description of the item variant object, see the object returned for the [`items`](/item-variants/variants-query-result/items) property in `VariantsQueryResult`.
 */
export function queryVariants(payload: object): RequestOptionsFactory<any> {
  function __queryVariants({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.menus.v1.item_variant',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.menus_item_variant.v1.VariantsService.QueryVariants',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsMenusItemVariantV1VariantsServiceUrl({
        protoPath: '/v1/variants/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'variants.createdDate' },
              { path: 'variants.updatedDate' },
              { path: 'variants.externalReferenceInfo.lastSyncDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryVariants;
}

/**
 * > **Note:** The Item API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Retrieves the number of item variants that match a specified filter.
 *
 * If a filter isn't passed in the request, the endpoint returns the count of all item variants.
 */
export function countVariants(payload: object): RequestOptionsFactory<any> {
  function __countVariants({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.menus.v1.item_variant',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.menus_item_variant.v1.VariantsService.CountVariants',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsMenusItemVariantV1VariantsServiceUrl({
        protoPath: '/v1/variants/count',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __countVariants;
}

/**
 * > **Note:** The Item Variants API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Deletes multiple item variants at once.
 */
export function bulkDeleteVariants(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkDeleteVariants({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.menus.v1.item_variant',
      method: 'DELETE' as any,
      methodFqn:
        'wix.restaurants.menus_item_variant.v1.VariantsService.BulkDeleteVariants',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsMenusItemVariantV1VariantsServiceUrl({
        protoPath: '/v1/bulk/variants/delete',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __bulkDeleteVariants;
}
